import style from "../../style/FinancialSummary.module.scss";
import className from "classnames/bind";
import {Fragment, useState, useEffect, memo, useCallback} from "react";
import {Table, DatePicker, Modal, Tooltip} from "antd";
import dayjs from "dayjs";
import ActionBar from "~/layout/ActionBar";
import {Loading} from "~/components";
import FontAwesomeIcon from "~/components/FontAwesome";
import {apiError, handleRequest, numberFormat, strToTime} from "~/utils";
import {reportApi} from "~/api";

const cn = className.bind(style);

//Hiển thị số: 0 -> '-'
const money = (value) => (value ? numberFormat(value) : '-');

//Màu chấm theo nhóm
const groupColor = {
	skd: '#0284c7',
	dh: '#7c3aed',
	fd: '#e11d48',
	giahan: '#f59e0b',
};

const emptyGroup = (name) => ({
	name,
	rev1: 0,
	rev2: 0,
	expenses: {vat: 0, fixed: 0, incidental: 0, service: 0, diplomatic: 0, other: 0},
	bonus: 0,
	payKD: 0,
	payHCVP: 0,
	profit: 0,
});

function FinancialSummaryReport() {

	const [loading, setLoading] = useState(false);

	const [time, setTime] = useState(dayjs().startOf('month'));

	const [groups, setGroups] = useState([]);

	const [total, setTotal] = useState(emptyGroup('TỔNG'));

	const [detail, setDetail] = useState({open: false, loading: false, title: '', kind: '', rows: [], total: 0});

	const openDetail = async (groupKey, groupName, type, label) => {
		setDetail({open: true, loading: true, title: `${groupName} — ${label}`, kind: '', rows: [], total: 0});
		const [error, response] = await handleRequest(reportApi.financialSummaryDetail({group: groupKey, type, time: strToTime(time)}));
		if(error) {
			setDetail((d) => ({...d, loading: false}));
			return;
		}
		setDetail((d) => ({
			...d,
			loading: false,
			kind: response.data?.kind || '',
			rows: (response.data?.rows || []).map((r, i) => ({...r, _k: i})),
			total: response.data?.total || 0,
		}));
	};

	const handleLoad = useCallback(async (t) => {
		setLoading(true);
		let [error, response] = await handleRequest(reportApi.financialSummary({time: strToTime(t)}));
		let message = apiError(`Lấy thông tin báo cáo thất bại`, error, response);
		if (!message) {
			setGroups(response.data?.groups || []);
			setTotal(response.data?.total || emptyGroup('TỔNG'));
		}
		setLoading(false);
	}, []);

	useEffect(() => { handleLoad(time).then(); }, [time, handleLoad]);

	//Tổng hợp các chỉ số cho thẻ card
	const e = total.expenses || {};
	//VAT không tính vào lợi nhuận -> không cộng vào tổng chi phí
	const costOnly = (e.fixed || 0) + (e.incidental || 0) + (e.service || 0) +
		(e.diplomatic || 0) + (e.other || 0) + (total.bonus || 0);
	const payAll = (total.payKD || 0) + (total.payHCVP || 0);
	const revenueTotal = (total.rev1 || 0) + (total.rev2 || 0);

	//Ô số click được -> mở modal chi tiết
	const cellLink = (type, label) => (value, row) => {
		if(!value) return money(value);
		return (
			<span
				onClick={() => openDetail(row.key, row.name, type, label)}
				style={{cursor: 'pointer', color: '#0284c7', textDecoration: 'underline dotted'}}
			>{money(value)}</span>
		);
	};

	//Ý nghĩa 2 cột doanh thu khác nhau theo từng nhóm
	const revLabel = (rowKey, which) => {
		if(rowKey === 'giahan') return which === 1 ? 'Doanh thu gia hạn host' : 'Doanh thu gia hạn khác';
		return which === 1 ? 'Doanh thu được tính doanh số' : 'Doanh thu không tính doanh số';
	};

	//Ô doanh thu: nhãn/tooltip theo đúng nhóm (gia hạn = host/khác)
	const revCell = (type, which) => (value, row) => {
		const label = revLabel(row.key, which);
		if(!value) return money(value);
		return (
			<Tooltip title={label}>
				<span
					onClick={() => openDetail(row.key, row.name, type, label)}
					style={{cursor: 'pointer', color: '#0284c7', textDecoration: 'underline dotted'}}
				>{money(value)}</span>
			</Tooltip>
		);
	};

	const columns = [
		{
			title: 'Kênh',
			dataIndex: 'name',
			fixed: 'left',
			width: 150,
			render: (value, row) => (
				<div className={cn('channel')}>
					<span className={cn('dot')} style={{background: groupColor[row.key] || '#94a3b8'}}/>
					{value}
				</div>
			),
		},
		{
			title: 'Tổng thu',
			children: [
				{title: 'Tính DS / Gia hạn host', dataIndex: 'rev1', width: 140, align: 'right', render: revCell('rev1', 1)},
				{title: 'Không tính DS / Gia hạn khác', dataIndex: 'rev2', width: 150, align: 'right', render: revCell('rev2', 2)},
			],
		},
		{
			title: 'Chi phí',
			children: [
				{title: 'Vat', dataIndex: ['expenses', 'vat'], width: 105, align: 'right', render: cellLink('exp_vat', 'Chi phí Vat')},
				{title: 'Cố định', dataIndex: ['expenses', 'fixed'], width: 105, align: 'right', render: cellLink('exp_fixed', 'Chi phí cố định')},
				{title: 'Phát sinh', dataIndex: ['expenses', 'incidental'], width: 105, align: 'right', render: cellLink('exp_incidental', 'Chi phí phát sinh')},
				{title: 'Dịch vụ', dataIndex: ['expenses', 'service'], width: 105, align: 'right', render: cellLink('exp_service', 'Chi phí dịch vụ')},
				{title: 'Ngoại giao', dataIndex: ['expenses', 'diplomatic'], width: 105, align: 'right', render: cellLink('exp_diplomatic', 'Chi phí ngoại giao')},
				{title: 'Khác', dataIndex: ['expenses', 'other'], width: 105, align: 'right', render: cellLink('exp_other', 'Chi phí khác')},
				{title: 'Thưởng', dataIndex: 'bonus', width: 115, align: 'right', render: cellLink('bonus', 'Thưởng')},
			],
		},
		{
			title: 'Lương',
			children: [
				{title: 'Lương KD', dataIndex: 'payKD', width: 120, align: 'right', render: cellLink('payKD', 'Lương kinh doanh')},
				{title: 'Lương HCVP', dataIndex: 'payHCVP', width: 120, align: 'right', render: cellLink('payHCVP', 'Lương HCVP')},
			],
		},
		{
			title: 'Lợi nhuận',
			dataIndex: 'profit',
			fixed: 'right',
			width: 140,
			align: 'right',
			render: (value) => <b style={{color: value < 0 ? '#ef4444' : '#0284c7'}}>{money(value)}</b>,
		},
	];

	const detailColumns = (kind) => {
		if(kind === 'revenue') return [
			{title: 'Khách hàng / HĐ', dataIndex: 'label'},
			{title: 'Nhân viên', dataIndex: 'sub', width: 160},
			{title: 'Dịch vụ', dataIndex: 'service', width: 110},
			{title: 'Số tiền', dataIndex: 'price', width: 140, align: 'right', render: money},
		];
		if(kind === 'extension') return [
			{title: 'Khách hàng / HĐ', dataIndex: 'label'},
			{title: 'Dịch vụ', dataIndex: 'service', width: 110},
			{title: 'Số tiền', dataIndex: 'price', width: 140, align: 'right', render: money},
		];
		if(kind === 'expense') return [
			{title: 'Hạng mục', dataIndex: 'label'},
			{title: 'Đối tượng', dataIndex: 'sub', width: 150},
			{title: 'Ngày chi', dataIndex: 'time', width: 110, render: (t) => t ? dayjs.unix(t).format('DD/MM/YYYY') : '-'},
			{title: 'Ghi chú', dataIndex: 'note'},
			{title: 'Số tiền', dataIndex: 'price', width: 140, align: 'right', render: money},
		];
		if(kind === 'salary') return [
			{title: 'Nhân viên', dataIndex: 'label'},
			{title: 'Phòng', dataIndex: 'sub', width: 180},
			{title: 'Tỷ lệ', dataIndex: 'percent', width: 80, align: 'right', render: (p) => (p || p === 0) ? `${p}%` : '-'},
			{title: 'Số tiền', dataIndex: 'price', width: 140, align: 'right', render: money},
		];
		return [
			{title: 'Nội dung', dataIndex: 'label'},
			{title: 'Số tiền', dataIndex: 'price', align: 'right', render: money},
		];
	};

	//Hàng tổng cố định dưới chân bảng
	const summary = () => {
		const cells = [
			total.rev1, total.rev2,
			e.vat, e.fixed, e.incidental, e.service, e.diplomatic, e.other, total.bonus,
			total.payKD, total.payHCVP,
		];
		return (
			<Table.Summary fixed>
				<Table.Summary.Row>
					<Table.Summary.Cell index={0}><b>TỔNG</b></Table.Summary.Cell>
					{cells.map((value, i) => (
						<Table.Summary.Cell key={i} index={i + 1} align="right">{money(value)}</Table.Summary.Cell>
					))}
					<Table.Summary.Cell index={cells.length + 1} align="right">
						<b style={{color: total.profit < 0 ? '#ef4444' : '#0284c7'}}>{money(total.profit)}</b>
					</Table.Summary.Cell>
				</Table.Summary.Row>
			</Table.Summary>
		);
	};

	const cards = [
		{icon: 'fa-light fa-sack-dollar', label: 'Doanh thu', value: revenueTotal, gradient: 'linear-gradient(135deg, #0369a1 0%, #0284c7 60%, #38bdf8 100%)'},
		{icon: 'fa-light fa-receipt', label: 'Chi phí (gồm thưởng)', value: costOnly, gradient: 'linear-gradient(135deg, #be123c 0%, #e11d48 60%, #fb7185 100%)'},
		{icon: 'fa-light fa-wallet', label: 'Quỹ lương', value: payAll, gradient: 'linear-gradient(135deg, #b45309 0%, #e47d0a 60%, #f59e0b 100%)'},
		{icon: 'fa-light fa-chart-line', label: 'Lợi nhuận', value: total.profit, gradient: 'linear-gradient(135deg, #047857 0%, #059669 60%, #34d399 100%)'},
	];

	return (
		<Fragment>
			<ActionBar title={'Báo cáo tài chính'}></ActionBar>
			<div className="container d-block">
				<div className={cn('page')}>

					<div className={cn('header')}>
						<DatePicker
							picker="month"
							allowClear={false}
							format="[Tháng] M / YYYY"
							value={time}
							onChange={(d) => d && setTime(d.startOf('month'))}
							style={{width: 170}}
						/>
					</div>

					<div className={cn('cards')}>
						{cards.map((c, i) => (
							<div key={i} className={cn('card')}>
								<div className={cn('icon')} style={{background: c.gradient}}>
									<FontAwesomeIcon icon={c.icon}/>
								</div>
								<div style={{minWidth: 0}}>
									<p className={cn('label')}>{c.label}</p>
									<p className={cn('value')}>{numberFormat(c.value)}</p>
									<p className={cn('unit')}>VND</p>
								</div>
							</div>
						))}
					</div>

					<div className={cn('panel')}>
						<div className={cn('panelHead')}>Tổng hợp doanh thu &amp; chi phí</div>
						<div className={cn('panelBody')}>
							{loading && <Loading/>}
							{!loading &&
								<div className={cn('table')}>
									<Table
										rowKey="key"
										size="small"
										bordered
										columns={columns}
										dataSource={groups}
										pagination={false}
										scroll={{x: 'max-content'}}
										summary={summary}
									/>
								</div>
							}
						</div>
					</div>

					<div className={cn('panel')}>
						<div className={cn('panelHead')}>Giải thích</div>
						<div className={cn('panelBody')}>
							<ul className={cn('legend')}>
								<li style={{'--dot': '#0284c7'}}><span><b>Tổng thu</b> — Sikido/D&amp;H/Fast Digital: cột 1 = doanh thu được tính doanh số, cột 2 = không tính doanh số (gồm cả phiếu thu thủ công, trừ phiếu thu VAT). Phòng gia hạn: cột 1 = host (host/ssl/email), cột 2 = doanh thu khác.</span></li>
								<li style={{'--dot': '#e11d48'}}><span><b>Chi phí</b> — gom theo ngày chi và loại chi phí (Vat, Cố định, Phát sinh, Dịch vụ, Ngoại giao, Khác), theo công ty. Riêng <b>VAT</b> chỉ hiển thị để theo dõi, <b>không tính vào lợi nhuận</b>.</span></li>
								<li style={{'--dot': '#f59e0b'}}><span><b>Thưởng</b> — phiếu chi loại thưởng, lấy theo kỳ thưởng của tháng đang xem.</span></li>
								<li style={{'--dot': '#7c3aed'}}><span><b>Lương KD</b> — Sikido/D&amp;H = phòng kinh doanh web; Fast Digital = phòng MKT sale.</span></li>
								<li style={{'--dot': '#0ea5e9'}}><span><b>Lương HCVP</b> — các phòng hành chính còn lại; Fast Digital = MKT kỹ thuật; Phòng gia hạn = phòng CSKH.</span></li>
								<li style={{'--dot': '#059669'}}><span><b>Lợi nhuận</b> = Tổng thu − (chi phí + thưởng + lương KD + lương HCVP).</span></li>
							</ul>
						</div>
					</div>

				</div>
			</div>

			<Modal
				open={detail.open}
				title={detail.title}
				footer={null}
				width={780}
				onCancel={() => setDetail((d) => ({...d, open: false}))}
			>
				{detail.loading ? <Loading/> :
					<Table
						rowKey="_k"
						size="small"
						columns={detailColumns(detail.kind)}
						dataSource={detail.rows}
						pagination={false}
						scroll={{y: 420}}
						locale={{emptyText: 'Không có dữ liệu'}}
						summary={() => {
							const cols = detailColumns(detail.kind);
							return (
								<Table.Summary fixed>
									<Table.Summary.Row>
										<Table.Summary.Cell index={0} colSpan={cols.length - 1}><b>Tổng</b></Table.Summary.Cell>
										<Table.Summary.Cell index={1} align="right"><b>{money(detail.total)}</b></Table.Summary.Cell>
									</Table.Summary.Row>
								</Table.Summary>
							);
						}}
					/>
				}
			</Modal>
		</Fragment>
	)
}

export default memo(FinancialSummaryReport);
